
import React, { useState } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface OptimizedImageProps extends Omit<HTMLMotionProps<"img">, 'onDrag' | 'onDragStart' | 'onDragEnd'> {
  src: string;
  alt: string;
  aspectRatio?: string;
  loadingClassName?: string;
  errorClassName?: string;
  showSkeleton?: boolean;
  layoutId?: string;
  enableMotion?: boolean;
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  aspectRatio = '1/1',
  className,
  loadingClassName,
  errorClassName,
  showSkeleton = true,
  layoutId,
  enableMotion = false,
  style,
  // Extract all motion props
  initial,
  animate,
  exit,
  transition,
  whileHover,
  whileTap,
  ...restProps
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setHasError(true);
  };

  // Enhanced motion props for layout animations
  const motionProps = enableMotion && layoutId ? {
    layoutId,
    initial: initial || { opacity: 0 },
    animate: animate || { opacity: 1 },
    exit: exit || { opacity: 0 },
    transition: transition || { 
      type: "spring", 
      stiffness: 300, 
      damping: 25,
      opacity: { duration: 0.2 }
    },
    whileHover,
    whileTap
  } : {
    initial: { opacity: 0 },
    animate: { opacity: isLoaded ? 1 : 0 },
    transition: { duration: 0.3, ease: "easeOut" }
  };

  return (
    <div className="relative w-full h-full" style={{ aspectRatio }}>
      {/* Skeleton loading state - only show if not loaded and skeleton enabled */}
      {!isLoaded && showSkeleton && !hasError && (
        <motion.div
          className={cn(
            "absolute inset-0 w-full h-full",
            loadingClassName
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-test-id="optimized-image-skeleton"
        >
          <Skeleton className="w-full h-full">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full"
              animate={{
                translateX: ['100%', '200%'],
                transition: {
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "linear"
                }
              }}
            />
          </Skeleton>
        </motion.div>
      )}
      
      {/* Error state */}
      {hasError && (
        <motion.div 
          className={cn(
            "absolute inset-0 w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800",
            errorClassName
          )}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          data-test-id="optimized-image-error"
        >
          <motion.span 
            className="text-sm text-gray-500 dark:text-gray-400"
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            Image not available
          </motion.span>
        </motion.div>
      )}
      
      {/* Main image with pure motion - no conflicting animations */}
      <motion.img
        {...motionProps}
        src={src}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        className={cn(
          className,
          hasError ? "invisible" : "visible"
        )}
        style={style}
        data-test-id="optimized-image"
        {...restProps}
      />
    </div>
  );
};
